import { Fragment, type ReactNode } from 'react'

import { cn } from '../lib/ui'

/**
 * Renders the Markdown subset an issue description actually contains.
 *
 * It emits REACT ELEMENTS and never touches dangerouslySetInnerHTML. That is
 * the whole design constraint: a description is written by one person and read
 * by their whole team, so a renderer that builds HTML from a string is an XSS
 * hole with extra steps. Nothing here can produce an element the code below did
 * not construct itself, so there is no sanitiser to get wrong and no HTML to
 * escape — React escapes every text node on the way out.
 *
 * The one place untrusted input still reaches an attribute is a link's href,
 * and that is filtered to http/https/mailto: `[click](javascript:…)` renders as
 * plain text rather than a live link.
 *
 * The supported subset is deliberately small — headings, lists, emphasis, code,
 * quotes, links, rules. It is what people write in a tracker. Anything outside
 * it degrades to the literal characters, which is the honest failure: the text
 * is still readable, it just is not styled.
 */

type Inline = { text: string; bold?: boolean; italic?: boolean; code?: boolean; strike?: boolean; href?: string }

/** Only these schemes may become a live href. */
function safeHref(raw: string): string | null {
  const url = raw.trim()
  // Protocol-relative and bare paths are fine; anything with a scheme is checked.
  if (/^(https?:\/\/|mailto:)/i.test(url)) return url
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) return null // javascript:, data:, vbscript:, …
  if (url.startsWith('/') || url.startsWith('#')) return url
  return null
}

/**
 * Splits one line into styled runs.
 *
 * Code spans are matched FIRST and never re-scanned, so `**not bold**` inside
 * backticks stays literal — which is the difference between showing someone
 * the syntax they asked about and silently eating it.
 */
function parseInline(text: string): Inline[] {
  const out: Inline[] = []
  const pattern =
    /(`[^`]+`)|(\[[^\]]*\]\([^)\s]+\))|(\*\*[^*]+\*\*)|(~~[^~]+~~)|(\*[^*\n]+\*)|(_[^_\n]+_)/
  let rest = text

  while (rest.length > 0) {
    const match = pattern.exec(rest)
    if (!match || match.index === undefined) {
      out.push({ text: rest })
      break
    }
    if (match.index > 0) out.push({ text: rest.slice(0, match.index) })
    const token = match[0]

    if (token.startsWith('`')) {
      out.push({ text: token.slice(1, -1), code: true })
    } else if (token.startsWith('[')) {
      const split = token.indexOf('](')
      const label = token.slice(1, split)
      const href = safeHref(token.slice(split + 2, -1))
      // An unsafe scheme keeps the original markdown as text rather than
      // quietly dropping it: the reader should see what was written.
      out.push(href ? { text: label || href, href } : { text: token })
    } else if (token.startsWith('**')) {
      out.push({ text: token.slice(2, -2), bold: true })
    } else if (token.startsWith('~~')) {
      out.push({ text: token.slice(2, -2), strike: true })
    } else {
      out.push({ text: token.slice(1, -1), italic: true })
    }
    rest = rest.slice(match.index + token.length)
  }
  return out.filter((run) => run.text !== '')
}

function Runs({ text }: { text: string }) {
  return (
    <>
      {parseInline(text).map((run, i) => {
        if (run.href) {
          return (
            <a
              key={i}
              href={run.href}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="text-brand-600 underline underline-offset-2 hover:text-brand-700 dark:text-brand-400"
            >
              {run.text}
            </a>
          )
        }
        if (run.code) {
          return (
            <code
              key={i}
              className="rounded bg-ink-100 px-1 py-0.5 font-mono text-[0.85em] text-ink-800 dark:bg-ink-800 dark:text-ink-100"
            >
              {run.text}
            </code>
          )
        }
        let node: ReactNode = run.text
        if (run.bold) node = <strong className="font-semibold">{node}</strong>
        if (run.italic) node = <em>{node}</em>
        if (run.strike) node = <s className="text-ink-500">{node}</s>
        return <Fragment key={i}>{node}</Fragment>
      })}
    </>
  )
}

type Block =
  | { kind: 'p'; lines: string[] }
  | { kind: 'h'; level: number; text: string }
  | { kind: 'ul' | 'ol'; items: string[] }
  | { kind: 'quote'; lines: string[] }
  | { kind: 'code'; lines: string[]; lang?: string }
  | { kind: 'hr' }

/** Groups lines into blocks. A fenced code block swallows everything until it closes. */
function parseBlocks(source: string): Block[] {
  const lines = source.replace(/\r\n?/g, '\n').split('\n')
  const blocks: Block[] = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]!

    if (/^\s*```/.test(line)) {
      const lang = line.replace(/^\s*```/, '').trim() || undefined
      const body: string[] = []
      i++
      while (i < lines.length && !/^\s*```/.test(lines[i]!)) {
        body.push(lines[i]!)
        i++
      }
      i++ // the closing fence, or the end of input
      blocks.push({ kind: 'code', lines: body, lang })
      continue
    }

    if (/^\s*$/.test(line)) {
      i++
      continue
    }

    if (/^\s{0,3}(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      blocks.push({ kind: 'hr' })
      i++
      continue
    }

    const heading = /^\s{0,3}(#{1,6})\s+(.*)$/.exec(line)
    if (heading) {
      blocks.push({ kind: 'h', level: heading[1]!.length, text: heading[2]!.trim() })
      i++
      continue
    }

    if (/^\s*>\s?/.test(line)) {
      const body: string[] = []
      while (i < lines.length && /^\s*>\s?/.test(lines[i]!)) {
        body.push(lines[i]!.replace(/^\s*>\s?/, ''))
        i++
      }
      blocks.push({ kind: 'quote', lines: body })
      continue
    }

    if (/^\s*([-*+])\s+/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^\s*([-*+])\s+/.test(lines[i]!)) {
        items.push(lines[i]!.replace(/^\s*([-*+])\s+/, ''))
        i++
      }
      blocks.push({ kind: 'ul', items })
      continue
    }

    if (/^\s*\d+[.)]\s+/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i]!)) {
        items.push(lines[i]!.replace(/^\s*\d+[.)]\s+/, ''))
        i++
      }
      blocks.push({ kind: 'ol', items })
      continue
    }

    const body: string[] = []
    while (
      i < lines.length &&
      !/^\s*$/.test(lines[i]!) &&
      !/^\s*([-*+]|\d+[.)])\s+/.test(lines[i]!) &&
      !/^\s{0,3}#{1,6}\s+/.test(lines[i]!) &&
      !/^\s*>\s?/.test(lines[i]!) &&
      !/^\s*```/.test(lines[i]!)
    ) {
      body.push(lines[i]!)
      i++
    }
    blocks.push({ kind: 'p', lines: body })
  }
  return blocks
}

export function Markdown({ source, className }: { source: string; className?: string }) {
  const blocks = parseBlocks(source)
  const headingSize = ['text-base', 'text-base', 'text-sm', 'text-sm', 'text-sm', 'text-sm']

  return (
    <div className={cn('space-y-2.5 text-sm leading-relaxed', className)}>
      {blocks.map((block, i) => {
        switch (block.kind) {
          case 'h':
            return (
              <h4
                key={i}
                className={cn(
                  'font-semibold tracking-tight text-ink-900 dark:text-ink-100',
                  headingSize[block.level - 1] ?? 'text-sm',
                  i > 0 && 'pt-1',
                )}
              >
                <Runs text={block.text} />
              </h4>
            )
          case 'ul':
            return (
              <ul key={i} className="list-disc space-y-1 pl-5 marker:text-ink-400">
                {block.items.map((item, j) => (
                  <li key={j}>
                    <Runs text={item} />
                  </li>
                ))}
              </ul>
            )
          case 'ol':
            return (
              <ol key={i} className="list-decimal space-y-1 pl-5 marker:text-ink-400 marker:tabular-nums">
                {block.items.map((item, j) => (
                  <li key={j}>
                    <Runs text={item} />
                  </li>
                ))}
              </ol>
            )
          case 'quote':
            return (
              <blockquote
                key={i}
                className="border-l-2 border-ink-200 pl-3 text-ink-600 dark:border-ink-700 dark:text-ink-300"
              >
                {block.lines.map((line, j) => (
                  <p key={j}>
                    <Runs text={line} />
                  </p>
                ))}
              </blockquote>
            )
          case 'code':
            return (
              // Wide code scrolls inside its own box; the panel must never
              // scroll sideways because somebody pasted a long line.
              <pre
                key={i}
                className="overflow-x-auto rounded-md bg-ink-50 p-3 font-mono text-xs leading-relaxed text-ink-800 dark:bg-ink-900 dark:text-ink-100"
              >
                <code>{block.lines.join('\n')}</code>
              </pre>
            )
          case 'hr':
            return <hr key={i} className="border-ink-200 dark:border-ink-800" />
          default:
            return (
              <p key={i}>
                {block.lines.map((line, j) => (
                  <Fragment key={j}>
                    {j > 0 && <br />}
                    <Runs text={line} />
                  </Fragment>
                ))}
              </p>
            )
        }
      })}
    </div>
  )
}
